import { useEffect, useRef } from "react";

import classnames from "classnames";
import type { ISelectedWell } from "stores/map";
import styled from "styled-components";

import usePlayTo3DDataPrivateSources from "hooks/usePlayTo3DDataPrivateSources";
import useUserModules from "hooks/useUserModules";

import { visibilityToggleStyles } from "components/shared/SharedStyles";

import { useXdaSettingsStore } from "./context";
import useMouseOver from "./context/useMouseover";
import { selectShowIpdb } from "./context/xdaSettingsSelectors";
import { VisToolbarOverflow, useVisToolbarFitCount } from "./toolbar/VisToolbarOverflow";
import {
  VisFullscreenToggle,
  VisScreenshotToggle,
  Xda3DGeoToggle,
  XdaDataToggle,
  XdaDucWellsToggle,
  XdaFilterToWellToggle,
  XdaGridToggle,
  XdaHangToggle,
  XdaMeasurementToggle,
  XdaOverlapToggle,
  XdaRadiusToggle,
  XdaResetSettings,
  XdaSettingsToggle,
  XdaSpacingToggle,
  XdaTooltipToggle,
  XdaTopsToggle,
  XdaValueInfoToggle
} from "./toolbar/xda";

interface XdaToolbarProps {
  mouseoverRef?: React.RefObject<HTMLDivElement>;
  binUpdateErrorMessage: string;
  onFullscreenToggle: (value) => void;
  availablePlays: string[];
  isLoading: boolean;
  noWellsAndSticks: boolean;
  selectedWells: { [name: string]: ISelectedWell };
  /**
   * EVA-185: UWIs of the dot-only wells in the current section, for "Filter to
   * wells" to include while the dots are showing. Empty when the toggle is off.
   */
  ducWells?: readonly string[];
  /** Set by hosts that own their own expand, such as the well card panel. */
  hideFullscreen?: boolean;
  /** Which viewer the camera puts into screenshot mode. */
  screenshotWidgetId: string;
  /**
   * The host renders this strip inside a toolbar row of its own instead of
   * letting it hover over the plot. It drops the overlay chrome and the hover
   * gate: a bar the user can see has no reason to wait for a mouse.
   */
  embedded?: boolean;
  /**
   * The element the host gives the strip to live in. It is what decides how
   * much room there is, so it is what the fit is measured against.
   */
  hostElement?: Maybe<HTMLElement>;
}

function XdaToolbar({
  mouseoverRef,
  binUpdateErrorMessage,
  onFullscreenToggle,
  availablePlays,
  isLoading,
  noWellsAndSticks,
  selectedWells,
  ducWells = [],
  hideFullscreen = false,
  embedded = false,
  hostElement = null,
  screenshotWidgetId
}: XdaToolbarProps) {
  const Components = [
    <Xda3DGeoToggle key="3dgeo" binUpdateErrorMessage={binUpdateErrorMessage} />,
    <XdaValueInfoToggle key="valueinfo" />,
    <Divider key="divider" />,
    <XdaRadiusToggle key="radius" />,
    <XdaOverlapToggle key="overlap" />,
    <XdaDucWellsToggle key="ducwells" />,
    <XdaHangToggle key="hang" />,
    <XdaTopsToggle key="tops" />,
    <XdaGridToggle key="grid" />,
    <XdaTooltipToggle key="tooltip" />,
    <XdaDataToggle key="data" />,
    <XdaFilterToWellToggle
      key="filtertowell"
      selectedWells={selectedWells}
      additionalWells={ducWells}
    />,
    <XdaMeasurementToggle key="measurement" />,
    <XdaSpacingToggle key="spacing" />,
    <VisScreenshotToggle
      key="screenshot"
      onToggle={onFullscreenToggle}
      type="xda"
      widgetId={screenshotWidgetId}
    />,
    <VisFullscreenToggle key="fullscreen" onToggle={onFullscreenToggle} />,
    <XdaResetSettings key="reset" />,
    <XdaSettingsToggle
      key="settings"
      availablePlays={availablePlays}
      isLoading={isLoading}
      noWellsAndSticks={noWellsAndSticks}
    />
  ];

  const showIpdb = useXdaSettingsStore(selectShowIpdb);

  const { has3dGeoModel } = useUserModules();
  const { data: privateDataSources, refetch: refetchPrivateDataSources } =
    usePlayTo3DDataPrivateSources();

  useEffect(() => {
    refetchPrivateDataSources();
  }, []);

  const geoModelEnabled = has3dGeoModel || !!privateDataSources?.length;

  // don't show 3d geo model related buttons if user doesn't have access
  // and if there are no private data sources
  if (!geoModelEnabled) {
    Components.splice(0, 3);
  } else if (!showIpdb) {
    Components.splice(1, 1);
  }

  // After the index-based splices above, which assume their own positions.
  if (hideFullscreen) {
    const fullscreenAt = Components.findIndex((c) => c.key === "fullscreen");
    if (fullscreenAt >= 0) {
      Components.splice(fullscreenAt, 1);
    }
  }

  const visible = useMouseOver(mouseoverRef);
  const wrapperClassnames = classnames({ visible: visible || embedded });

  // Docked in a host's bar the strip has a width to fit; floating over the plot
  // it does not, and every button stays inline exactly as before.
  const stripRef = useRef<HTMLDivElement>(null);
  const fits = useVisToolbarFitCount({
    host: hostElement,
    stripRef,
    items: Components,
    enabled: embedded
  });
  // Nothing overflowing renders the array as it always did, untouched.
  const overflowing = fits < Components.length;
  const inline = overflowing
    ? trimTrailingDivider(Components.slice(0, fits))
    : Components;
  const overflowed = overflowing ? dropDividers(Components.slice(fits)) : [];

  return (
    <Wrapper ref={stripRef} className={wrapperClassnames} $embedded={embedded}>
      {inline}
      {overflowed.length > 0 && <VisToolbarOverflow items={overflowed} />}
    </Wrapper>
  );
}

export default XdaToolbar;

/**
 * A rule is a grouping between buttons, so one left at the end of the inline
 * row divides the row from the overflow trigger and reads as noise — the chart
 * toolbar drops its own trailing divider for the same reason.
 */
const trimTrailingDivider = (items: JSX.Element[]): JSX.Element[] =>
  String(items[items.length - 1]?.key) === "divider" ? items.slice(0, -1) : items;

/** A menu is a list of actions; a rule has nothing to divide in it. */
const dropDividers = (items: JSX.Element[]): JSX.Element[] =>
  items.filter((item) => String(item.key) !== "divider");

const Wrapper = styled.div<{ $embedded?: boolean }>`
  ${visibilityToggleStyles};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 0 8px;
  height: var(--chart-toolbar-height);
  display: flex;
  align-items: center;
  justify-content: right;
  gap: 8px;
  background-color: var(--white);
  box-shadow: 0 14px 16px 0 rgb(45 53 63 / 10%),
    inset 0px 7px 8px -10px rgb(45 53 63 / 15%);
  z-index: 998;
  padding-right: 4px;

  /* Sits in the host's bar: no lift off the plot, no fade, nothing to
     raise. It stays one row at the host's width; the buttons that do not fit
     go behind the overflow menu instead of wrapping or being clipped.

     NOTE: no backticks in this comment — it sits inside a
     styled-components template literal, and one would terminate it. */
  ${(props) =>
    props.$embedded &&
    `
    position: static;
    height: 100%;
    padding: 0;
    background-color: transparent;
    box-shadow: none;
    transition: none;
    z-index: auto;
  `}
`;

const Divider = styled.div`
  width: 1px;
  height: var(--chart-toolbar-height);
  background-color: #d9e1e2;
`;
